import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { resolveNavigation } from '@platform/entitlements';
import type { PortalModule } from '../../api';
import { ModulePlaceholder } from '../ModulePlaceholder';
import { SectionIndex } from '../SectionIndex';
import { shouldSkipStart } from './skipStart';
import { PortfolioDefinitionStartPage } from './StartPage';
import { ProcessHierarchy } from './settings/ProcessHierarchy';
import { FrameworksList } from './settings/FrameworksList';
import { RecommendationScoring } from './settings/RecommendationScoring';
import { FrameworkBuilder } from './settings/FrameworkBuilder';
import { FrameworkDesigner } from './settings/FrameworkDesigner';
import { OpportunityAttributes } from './settings/OpportunityAttributes';
/*
  Settings -> Project Execution reaches ACROSS to the Project Management module rather than
  copying its two screens here.

  They were cloned during that port, they talk to `/api/project_management/settings/
  project-execution/*`, and they configure `delivery_project_access` +
  `delivery_project_screen_permissions` — one set of tables, one pair of screens. A second
  copy under this folder would be two files to keep in step, and CLAUDE.md's whole account
  of how screens go wrong is about copies drifting apart.

  The same branch still appears under Project Management -> Settings. It was not named for
  removal (CLAUDE.md, additive change only), so it stays until the owner rules on it.
*/
import { ProjectVisibility } from '../project_management/settings/ProjectVisibility';
import { ProjectRegistry } from './projects/ProjectRegistry';
import { ProjectAdministration } from './projects/ProjectAdministration';
import { ProjectsDashboard } from './projects/Dashboards';
import { ScoringDashboard } from './opportunities/ScoringDashboard';
import { ExecutiveDashboard } from './executive/ExecutiveDashboard';
import { OpportunityRegistry } from './opportunities/OpportunityRegistry';
import { ScoringOverview } from './opportunities/ScoringQueues';
/* The Scoring Queue is its OWN screen, cloned from the source's project-grouped tree —
   NOT the flat `mode`-driven table, which stays for in-progress and completed. See
   ScoringQueue.tsx for why they were split back apart (CLAUDE.md §1.2.1). */
import { ScoringQueue } from './opportunities/ScoringQueue';
import { ScoringInProgress } from './opportunities/ScoringInProgress';
import { ScoringCompleted } from './opportunities/ScoringCompleted';
import { ScoringAuditTrail } from './opportunities/ScoringAuditTrail';
import { ScoringForm } from './opportunities/ScoringForm';
import { HeatMap } from './opportunities/HeatMap';
import { OpportunityForm } from './opportunities/OpportunityForm';
import { DeliveryPathway } from './delivery/DeliveryPathway';
/* Program Quick Start's Analysis & Preview screen, built to
   `docs/program-quick-start-analysis-preview-design-spec.md` Parts 1-9. The previous
   `ProgramQuickStart` is left on disk, unrouted — it was not named for deletion. */
import { QuickStartPreview } from './quickstart/QuickStartPreview';
import { QuickStartAnalysis } from './quickstart/QuickStartAnalysis';
import { ScenarioBuilder } from './optimizer/ScenarioBuilder';
import { FinancialConfiguration, ScenarioSetup } from './financial/Configuration';
import { CostScenarios } from './financial/CostScenarios';
import { FinancialSummary, VersionComparison } from './financial/FinancialSummary';
import { NpvCalculator, NpvComparison } from './financial/Npv';
import { SavedScenarios, OptimizerAnalytics } from './optimizer/SavedScenarios';
import { CompareScenarios } from './optimizer/CompareScenarios';
import { UserGuide } from './help/UserGuide';
import { HelpSupport } from './help/HelpSupport';

/**
 * Inventory Registry — screen resolution, the same shape APR, AI Readiness and SPN use.
 *
 * Keyed by the section-key trail, so a route is derived from the catalogue rather than
 * written twice. A key with no entry here falls through to `ModulePlaceholder`, which is
 * how a nav row for a screen that is still being built behaves — visible, navigable, and
 * honest about being empty, rather than a dead link or a crash.
 */
const MOVED: Record<string, string> = {
  '/portfolio_definition/opportunities/dashboard':
    '/portfolio_definition/opportunities/scoring-dashboard',
};

const SCREENS: Record<string, () => JSX.Element> = {
  'program-quick-start': () => <QuickStartPreview />,
  /* The Analysis browser — its own screen, because the review body could not be filtered
     on the intake screen once a run held 90 projects. §5. */
  'quick-start-analysis': () => <QuickStartAnalysis />,
  'projects/project-registry': () => <ProjectRegistry />,
  'projects/project-administration': () => <ProjectAdministration />,
  'projects/dashboard': () => <ProjectsDashboard />,
  'opportunities/scoring-dashboard': () => <ScoringDashboard />,
  /* Last before Settings — after Portfolio Optimizer, the owner's placement (2026-09-30). */
  'executive-dashboard': () => <ExecutiveDashboard />,
  'opportunities/opportunity-registry': () => <OpportunityRegistry />,
  'opportunities/scoring': () => <ScoringOverview />,
  'opportunities/scoring-queue': () => <ScoringQueue />,
  'opportunities/in-progress': () => <ScoringInProgress />,
  'opportunities/completed': () => <ScoringCompleted />,
  'opportunities/audit-trail': () => <ScoringAuditTrail />,
  'delivery-pathway': () => <DeliveryPathway />,
  'financial-modeling/configuration': () => <FinancialConfiguration />,
  'financial-modeling/mvp-value-model/cost-scenarios': () => <CostScenarios context="MVP" />,
  'financial-modeling/mvp-value-model/financial-summary': () => <FinancialSummary />,
  'financial-modeling/mvp-value-model/version-comparison': () => <VersionComparison />,
  'financial-modeling/npv-value-model/npv-cost-scenarios': () => <CostScenarios context="NPV" />,
  'financial-modeling/npv-value-model/npv-calculator': () => <NpvCalculator />,
  'financial-modeling/npv-value-model/scenario-comparison': () => <NpvComparison />,
  'financial-modeling/scenario-setup': () => <ScenarioSetup />,
  'portfolio-optimizer/strategy-and-constraints': () => <ScenarioBuilder />,
  'portfolio-optimizer/saved-scenarios': () => <SavedScenarios />,
  'portfolio-optimizer/analytics-visuals': () => <OptimizerAnalytics />,
  'settings/business-process-definition/l1-l3-process-hierarchy': () => <ProcessHierarchy />,
  'settings/assessment-framework/frameworks': () => <FrameworksList />,
  'settings/assessment-framework/recommendation': () => <RecommendationScoring />,
  'settings/assessment-framework/framework-builder': () => <FrameworkBuilder />,
  'settings/assessment-framework/framework-designer': () => <FrameworkDesigner />,
  'settings/opportunity-attributes': () => <OpportunityAttributes />,
  'settings/project-execution/project-visibility': () => <ProjectVisibility />,
  /*
    `simple` — Full access / View-only, no per-screen matrix. The owner, 2026-08-09: the 17
    execution screens the matrix lists are Project Management's, and *"we do not have any of
    those screens in this module (Inventory Registry)"*. Project Management renders the same
    component with its default `full` variant.
  */
  /*
    ONE SCREEN, FOUR TABS. These were four routes and four near-identical files; the owner
    commissioned the consolidation and approved a prototype (2026-08-09). What made them
    four files — each attribute's own extra fields, its own help panel, and Delivery Risk's
    inverted scale — is declared per tab inside the screen rather than flattened away.

    The four component files are left on disk, unreferenced. They are ported code and
    deleting them was not asked for; say the word and they go.
  */
  'user-guide': () => <UserGuide />,
  'help-support': () => <HelpSupport />,
};

export interface PortfolioDefinitionModuleProps {
  modules: PortalModule[];
  /** Greets the user, as the source's start screen does. */
  firstName?: string;
}

export function PortfolioDefinitionModule({ modules, firstName }: PortfolioDefinitionModuleProps) {
  const module = modules.find((m) => m.id === 'portfolio_definition');
  if (!module || module.sections.length === 0) return <Navigate to="/" replace />;
  return <Content modules={modules} module={module} firstName={firstName} />;
}

function Content({
  modules,
  module,
  firstName,
}: {
  modules: PortalModule[];
  module: PortalModule;
  firstName?: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  /*
   * Paths that MOVED. 'Dashboard' was renamed 'Scoring Dashboard' on the owner's
   * instruction (2026-09-30), and a label change moves the route with it — so a bookmark or
   * a link to the old path would 404. Redirected rather than left to break, the same way
   * Project Management handles its renames.
   */
  const moved = MOVED[location.pathname.replace(/\/+$/, '')];
  if (moved) return <Navigate to={`${moved}${location.search}`} replace />;

  const at = resolveNavigation(location.pathname, [module]);

  // `/portfolio_definition` is the product's own Start Menu — entering the module lands
  // there, not on a list of buckets, matching APR and SPN.
  if (at.trail.length === 0) {
    const skipTo = shouldSkipStart(location.search);
    if (skipTo) return <Navigate to={skipTo} replace />;
    return <PortfolioDefinitionStartPage firstName={firstName} />;
  }

  const deepest = at.trail[at.trail.length - 1]!;

  if (!at.section) {
    return (
      <SectionIndex
        title={deepest.label}
        sections={deepest.sections ?? []}
        onOpen={(path) => navigate(path)}
      />
    );
  }

  const key = at.trail.map((s) => s.key).join('/');

  // `/…/scoring-queue/<opportunity-id>` opens the Q&A form for one opportunity. It is a
  // sub-path of the queue screen, not a catalogue section, so the id is read off the
  // path — the same pattern SPN uses for a discovery session code.
  if (key === 'opportunities/opportunity-registry') {
    const rest = location.pathname.slice(at.section.path.length + 1).split('/').filter(Boolean);
    if (rest[0] === 'new') return <OpportunityForm />;
    if (rest[0] && rest[1] === 'edit') {
      return <OpportunityForm opportunityId={decodeURIComponent(rest[0])} />;
    }
    /*
     * VIEW IS THE SAME SCREEN, FROZEN. The owner's instruction was that viewing an
     * opportunity must use the same layout as editing it — the source renders both from
     * one view (`opportunities-form.ejs`), switching on `canFullEdit`, and this restores
     * that. `readOnly` additionally hides Save, Delete, Submit, Revert and the
     * unlink-from-project control, which is edit-only by the owner's ruling.
     *
     * `OpportunityDetail.tsx` is what used to render here. It is left in the tree rather
     * than deleted; its two unique controls (assign framework, revert to draft) were moved
     * onto this screen first, so nothing it offered was lost.
     */
    if (rest[0]) return <OpportunityForm opportunityId={decodeURIComponent(rest[0])} readOnly />;
  }

  if (key === 'opportunities/scoring') {
    const rest = location.pathname.slice(at.section.path.length + 1).split('/').filter(Boolean);
    if (rest[0] === 'heatmap') return <HeatMap />;
  }

  /*
   * Compare Scenarios is a SUB-PATH of Saved Scenarios, not its own nav row: it is reached
   * from there, it carries the ids it compares in the query string, and the source app has
   * no menu entry for it either.
   */
  if (key === 'portfolio-optimizer/saved-scenarios') {
    const rest = location.pathname.slice(at.section.path.length + 1).split('/').filter(Boolean);
    if (rest[0] === 'compare') return <CompareScenarios />;
  }

  if (key === 'opportunities/scoring-queue') {
    const rest = location.pathname.slice(at.section.path.length + 1).split('/').filter(Boolean);
    if (rest[0]) return <ScoringForm opportunityId={decodeURIComponent(rest[0])} />;
  }

  const render = SCREENS[key];
  if (!render) {
    return (
      <ModulePlaceholder
        modules={modules}
        module={module}
        section={{ key: at.section.key, label: at.section.label }}
      />
    );
  }
  return render();
}
